/**
 * Home Hero Block Frontend Script
 *
 * Handles video playback and user interactions
 */
'use strict';

/**
 * State storage for video elements
 * @type {WeakMap<HTMLVideoElement, Object>}
 */
const videoStates = new WeakMap();

/**
 * Initialize state object for a video element
 *
 * @param {HTMLVideoElement} video Video element
 * @return {Object} State object
 */
function initializeVideoState( video ) {

	return {
		hasAutoplay: video.hasAttribute( 'autoplay' ),
		userPaused: false,
		isInitialLoad: true,
		initialLoadComplete: false
	};

}

/**
 * Setup video viewport observation for performance optimization
 *
 * @param {HTMLVideoElement} video Video element to observe
 * @param {HTMLElement} btnPlay Play button element
 * @param {HTMLElement} btnPause Pause button element
 */
function setupVideoIntersectionObserver( video, btnPlay, btnPause  ) {

	if ( ! ( 'IntersectionObserver' in window ) ) {

		return;

	}

	// Initialize state for this video.
	const state = initializeVideoState( video );
	videoStates.set( video, state );

	// Grace period for initial page load (prevent immediate pause).
	setTimeout( () => {

		const currentState = videoStates.get( video );
		if ( currentState ) {

			currentState.isInitialLoad = false;
			currentState.initialLoadComplete = true;

		}

	}, 2000 );

	const observer = new IntersectionObserver( ( entries ) => {

		entries.forEach( ( entry ) => {

			const currentState = videoStates.get( video );
			if ( ! currentState ) {

				return;

			}

			// Video entering viewport.
			if ( entry.isIntersecting ) {

				// Resume if: has autoplay AND not user-paused AND grace period passed.
				if ( currentState.hasAutoplay &&
					! currentState.userPaused &&
					currentState.initialLoadComplete ) {

					video.play().catch( ( err ) => {

						// Handle autoplay policy restrictions.
						// eslint-disable-next-line no-console
						console.warn( 'Video autoplay failed:', err );

					} );

					btnPlay?.classList.remove( 'video-button--active' );
					btnPause?.classList.add( 'video-button--active' );

				}

			} else if ( ! currentState.isInitialLoad ) {

				// Video leaving viewport - only pause if initial load grace period has passed.
				video.pause();
				btnPlay?.classList.add( 'video-button--active' );
				btnPause?.classList.remove( 'video-button--active' );

			}

		} );

	}, {
		threshold: 0.25
	} );

	observer.observe( video );

}

/**
 * Initialize all hero videos on the page
 */
function initHeroVideos() {

	const heroBlocks = document.querySelectorAll( '.b-home-hero' );

	heroBlocks.forEach( ( block ) => {

		const heroVideos = block.querySelectorAll( '.b-home-hero__video' );
		const btnPlay    = block.querySelector( '.video-button--play' );
		const btnPause   = block.querySelector( '.video-button--pause' );

		heroVideos.forEach( ( video ) => {

			btnPlay.addEventListener( 'click', () => {

				video.play();

				// Clear user-paused flag (allow auto-resume).
				const state = videoStates.get( video );
				if ( state ) {

					state.userPaused = false;

				}

				btnPlay?.classList.remove( 'video-button--active' );
				btnPause?.classList.add( 'video-button--active' );

			} )

			btnPause.addEventListener( 'click', () => {

				video.pause();

				// Mark user-paused (prevent auto-resume).
				const state = videoStates.get( video );
				if ( state ) {

					state.userPaused = true;

				}

				btnPlay?.classList.add( 'video-button--active' );
				btnPause?.classList.remove( 'video-button--active' );

			} )

			// Setup viewport observation for performance.
			setupVideoIntersectionObserver( video, btnPlay, btnPause );

		} );

	} );

}

// Initialize home hero blocks.
initHeroVideos();
